/* eslint-disable */
// @ts-ignore
import React, { useEffect, useState } from "react";
import { UseFormLoaderProps, useFormLoader } from "./hooks/useFormLoader";
// import "./Form.scss"
import { FormStorage, IdentityInfo, StepBuilder, initFormState, type FormElementPropertiesBase } from "@episerver/forms-sdk";
import { FormProvider } from "./context/FormProvider";
import { FormBodyPersonalDetail } from "./components/FormBodyPersonalDetail";
import { useDonationStore } from "4-sections/DonationBlock/libs/useDonationStore";
import LoadingIndicator from "3-components/LoadingIndicator/LoadingIndicator";
import queryString from "query-string";
import { useDonationBlockProps } from "4-sections/DonationBlock/DonationBlockProvider";
import { CRM_DONATION_TYPE } from "4-sections/DonationBlock/libs/common";
import { useDonationTracker } from "4-sections/DonationBlock/libs/useDonationTracker";
import { CustomFormContainerProperties, CustomFormContainer } from "./types/form";
import { PAYMENT_TYPE } from "3-components/Donations/libs/common";

interface FormProps {
    /**
     * The form key that identifies the form
     */
    formKey: string;
    /**
     * The code of the form language
     */
    language?: string;
    /**
     * The base url of Headless Form API
     */
    baseUrl: string;
    /**
     * Access token for form submit
     */
    identityInfo?: IdentityInfo;
    /**
     * The instance of useHistory() received from react-router-dom
     */
    history?: any;
    /**
     * The public url of current page
     */
    currentPageUrl?: string;
    /**
     * The endpoint url of Optimizely Graph 
     */
    optiGraphUrl?: string;
}

interface FormElementProperties extends FormElementPropertiesBase {
  predefinedValue: string;
}

export function FormPersonalDetail({formKey, language, baseUrl, identityInfo, history, currentPageUrl, optiGraphUrl}: FormProps){

  const { data: formData, loading: isLoading } = useFormLoader({ formKey, language, baseUrl, optiGraphUrl } as UseFormLoaderProps);

  const { baseURL, tracker, donationConfig } = useDonationBlockProps();

  const [isLoadingPaypal, setIsLoadingPaypal] = useState(true);

  const [predefinedValue, setPredefinedValue] = useState({
    first_name: '',
    last_name: '',
    email: '',
    paypalAuthId: ''
  });

  const parsedQueryUrl = queryString.parse(window.location.search);

  const { donationData: { frequency, fee, payment }, getTotalAmount } = useDonationStore();

  const { gaId } = useDonationTracker();

  const utmParams = sessionStorage.getItem('utmParams');
  const utmParamsObj = utmParams ? JSON.parse(utmParams) : {};

  const utmSource = utmParamsObj?.utm_source;
  const utmCampaign = utmParamsObj?.utm_campaign;
  const utmMedium = utmParamsObj?.utm_medium;

  const [isPayPal, setIsPayPal] = useState(false);

  useEffect(() => {
    if(parsedQueryUrl?.token && parsedQueryUrl?.PayerID){
      setIsPayPal(true);
    } else {
      setIsLoadingPaypal(false);
    }
  }, [parsedQueryUrl]);

  useEffect(() => {
    if(isPayPal) {
      const getPaypalDetail = async () => {
        const formdata = new FormData();
        formdata.append('OrderId', parsedQueryUrl?.token as string);

        const requestData = await fetch(`${baseURL}/api/PayPalV2/Authorize`, {
          method: 'POST',
          body: formdata
        });

        const result = await requestData.json();

        setPredefinedValue({
          first_name: result.PayPalPersonalDetails.Payer.name.given_name,
          last_name: result.PayPalPersonalDetails.Payer.name.surname,
          email: result.PayPalPersonalDetails.Payer.email_address,
          paypalAuthId: result.AuthorizationId
        })

        setIsLoadingPaypal(false);
      }

      getPaypalDetail()
    }

  }, [isPayPal])

  if(isLoading || isLoadingPaypal){
    return <LoadingIndicator />
  }
  
  if (!formData) {
    return <></>;
  }

  const stepBuilder = new StepBuilder(formData);
  const form = stepBuilder.buildForm();
  const formStorage = new FormStorage(form);
  const formStorageSubmission = formStorage.loadFormDataFromStorage();

  /**
   * Below are the fields related to donation
   * First name, last name, email and PaypalAuthorizationId will be prefilled only for Paypal payment flow
   */
  const amountField = form.formElements.find(formField => formField.displayName == 'DonationAmount');
  const frequencyField = form.formElements.find(formField => formField.displayName == 'DonationFrequency');
  const paymentMethodField = form.formElements.find(formField => formField.displayName == 'PaymentMethod');
  const fName = form.formElements.find(formField => formField.displayName == 'First name');
  const lName = form.formElements.find(formField => formField.displayName == 'Last name');
  const emailField = form.formElements.find(formField => formField.displayName == 'Email');
  const paypalAuthField = form.formElements.find(formField => formField.displayName == 'PayPalAuthorizationId');

  /**
   * Below are additional fields used by CRM
   * to record where donation have been made
   * - LandingPageCode will be filled with campaign code
   * - WebsiteSource will be filled with locale, e.g en-AU, en-HK
   */
  const landingPageCodeField = form.formElements.find(formField => formField.displayName == 'LandingPageCode');
  const gaClientId = form.formElements.find(formField => formField.displayName == 'ClientId');
  const adminFeeOptField = form.formElements.find(formField => formField.displayName == 'AdminFeeOptIn');
  const utmSourceField = form.formElements.find(formField => formField.displayName == 'UTMSource');
  const utmMediumField = form.formElements.find(formField => formField.displayName == 'UTMMedium');
  const utmCampaignField = form.formElements.find(formField => formField.displayName == 'UTMCampaign');
  const webSourceField = form.formElements.find(formField => formField.displayName == 'WebsiteDescription');
  const pageNameField = form.formElements.find(formField => formField.displayName == 'LandingPageDescription');

  if(amountField) {
    const totalAmount = getTotalAmount();
    amountField.properties = {
      ...amountField.properties,
      predefinedValue: parseFloat(totalAmount.toString()).toFixed(2)
    } as FormElementProperties;

    formStorageSubmission.map((submission) => {
      if(submission.elementKey === amountField.key) {
        submission.value = parseFloat(totalAmount.toString()).toFixed(2);
      } 
    });
  }

  if(frequencyField) {
    frequencyField.properties = {
      ...frequencyField.properties,
      predefinedValue: CRM_DONATION_TYPE[frequency]
    } as FormElementProperties;

    formStorageSubmission.map((submission) => {
      if(submission.elementKey === frequencyField.key) {
        submission.value = CRM_DONATION_TYPE[frequency]
      } 
    })
  }

  if(paymentMethodField) {
    const isCreditCardProvider = payment !== PAYMENT_TYPE.DIRECT_DEBIT && payment !== PAYMENT_TYPE.PAYPAL;
    
    paymentMethodField.properties = {
      ...paymentMethodField.properties,
      predefinedValue: isCreditCardProvider ? PAYMENT_TYPE.CREDIT_CARD : payment
    } as FormElementProperties;

    formStorageSubmission.map((submission) => {
      if(submission.elementKey === paymentMethodField.key) {
        submission.value = isCreditCardProvider ? PAYMENT_TYPE.CREDIT_CARD : payment
      } 
    })
  }

  if(predefinedValue.first_name && fName) {
    fName.properties = {
      ...fName.properties,
      predefinedValue: predefinedValue.first_name
    } as FormElementProperties;
  }

  if(predefinedValue.last_name && lName) {
    lName.properties = {
      ...lName.properties,
      predefinedValue: predefinedValue.last_name
    } as FormElementProperties;
  }

  if(predefinedValue.email && emailField) {
    emailField.properties = {
      ...emailField.properties,
      predefinedValue: predefinedValue.email
    } as FormElementProperties;
  }

  if(predefinedValue.paypalAuthId && paypalAuthField) {
    paypalAuthField.properties = {
      ...paypalAuthField.properties,
      predefinedValue: predefinedValue.paypalAuthId
    } as FormElementProperties;
  }

  if(gaClientId) {
    // this is crm field to record Google Analytic client id
    gaClientId.properties = {
      ...gaClientId.properties,
      predefinedValue: gaId
    } as FormElementProperties;
  }

  if(adminFeeOptField && fee) {
    // this is crm field to record if user include donation fee in their donation amount
    // set the value to 'YES' if true
    // and leave it blank if false
    adminFeeOptField.properties = {
      ...adminFeeOptField.properties,
      predefinedValue: 'YES'
    } as FormElementProperties;
  }

  if(utmSourceField) {
    // set utm source value for tracker
    utmSourceField.properties = {
      ...utmSourceField.properties,
      predefinedValue: utmSource
    } as FormElementProperties;
  }

  if(utmMediumField) {
    // set utm medium value for tracker
    utmMediumField.properties = {
      ...utmMediumField.properties,
      predefinedValue: utmMedium
    } as FormElementProperties;
  }

  if(utmCampaignField) {
    // set utm campaign value for tracker
    utmCampaignField.properties = {
      ...utmCampaignField.properties,
      predefinedValue: utmCampaign
    } as FormElementProperties;
  }

  if(webSourceField) {
    webSourceField.properties = {
      ...webSourceField.properties,
      predefinedValue: donationConfig?.locale
    } as FormElementProperties;
  }

  if(pageNameField) {
    pageNameField.properties = {
      ...pageNameField.properties,
      predefinedValue: tracker?.landingPageDescription
    } as FormElementProperties;
  }

  if(tracker?.landingPageCode && tracker?.landingPageCode !== ''  && landingPageCodeField) {
    landingPageCodeField.properties = {
      ...landingPageCodeField.properties,
      predefinedValue: tracker.landingPageCode
    } as FormElementProperties;
  }

  formStorage.saveFormDataToStorage(formStorageSubmission);
  const state = initFormState(form, currentPageUrl, history);
  // indentify the form for GA4 dataLayer
  ( state.formContainer.properties as CustomFormContainerProperties ).name = 'Donation Personal Detail Form';
  ( state.formContainer as CustomFormContainer ).type = 'donation';

  {/* finally return the form */}
  return (
      <FormProvider initialState={state}>
          <FormBodyPersonalDetail identityInfo={identityInfo} baseUrl={baseUrl} history={history} currentPageUrl={currentPageUrl}/>
      </FormProvider>
  )
}